import {
  Equipment,
  EquipmentMetadataResponse,
  Technology,
  EquipmentStats,
  EDTField,
} from "../models/EquipmentTypes";

const FO_TECHNOS = [
  "aquasav",
  "berkeslt",
  "controlunit",
  "customfo",
  "elgamedica",
  "eplchorus",
  "eplfxquest",
  "evaledpc",
  "evaledrv",
  "hardness",
  "hydrex",
  "hydrexwcm",
  "ionpro",
  "nurion",
  "orion",
  "poolfilter",
  "rapidstrata",
  "sirionmgsf",
  "sirionproadv",
  "sirionro30",
  "tank",
  "terionde",
  "terionro40",
  "thmposiris",
  "uflexpool",
];

const BASE_PATH = "/configs";

interface EDTFieldsIndex {
  ctrl_ids: Record<string, string[]>;
  edt_fields: Record<string, EDTField>;
}

const getTechnologies = (): Technology[] => {
  const technologies: Technology[] = [];

  FO_TECHNOS.forEach((name) => {
    technologies.push({
      name,
      category: "fo",
      path: `${BASE_PATH}/${name}.json`,
    });
  });

  return technologies;
};

const fetchEquipment = async (path: string): Promise<Equipment[]> => {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      console.warn(`Failed to fetch ${path}: ${response.status}`);
      return [];
    }
    const data: EquipmentMetadataResponse = await response.json();
    return data.data || [];
  } catch (error) {
    console.warn(`Error loading ${path}:`, error);
    return [];
  }
};

const fetchEDTFields = async (
  techName: string,
): Promise<{
  ctrlIds: Record<string, string[]>;
  edtFields: Record<string, EDTField>;
}> => {
  try {
    const response = await fetch(`${BASE_PATH}/${techName}-edt-fields.json`);
    if (!response.ok) {
      return { ctrlIds: {}, edtFields: {} };
    }
    const data: EDTFieldsIndex = await response.json();
    return {
      ctrlIds: data.ctrl_ids || {},
      edtFields: data.edt_fields || {},
    };
  } catch (error) {
    console.warn(`Error loading EDT fields for ${techName}:`, error);
    return { ctrlIds: {}, edtFields: {} };
  }
};

const loadAllEquipment = async (): Promise<Record<string, Equipment[]>> => {
  const technologies = getTechnologies();
  const results: Record<string, Equipment[]> = {};

  const edtFieldsPromises = technologies.map(async (tech) => {
    const result = await fetchEDTFields(tech.name);
    return { name: tech.name, ...result };
  });

  const edtFieldsResults = await Promise.all(edtFieldsPromises);
  const ctrlIdsMap: Record<string, Record<string, string[]>> = {};
  const edtFieldsMap: Record<string, Record<string, EDTField>> = {};
  edtFieldsResults.forEach(({ name, ctrlIds, edtFields }) => {
    ctrlIdsMap[name] = ctrlIds;
    edtFieldsMap[name] = edtFields;
  });

  const promises = technologies.map(async (tech) => {
    const equipment = await fetchEquipment(tech.path);
    equipment.forEach((eq) => {
      const controllerId = eq.equipment_controller_id;
      if (!controllerId) return;

      const edtFieldIds = ctrlIdsMap[tech.name]?.[controllerId];
      if (edtFieldIds && edtFieldIds.length > 0) {
        const fields: EDTField[] = [];
        edtFieldIds.forEach((id) => {
          const field = edtFieldsMap[tech.name]?.[id];
          if (field) fields.push(field);
        });
        if (fields.length > 0) {
          eq.edt_fields = fields;
        }
      }
    });
    return { name: tech.name, equipment };
  });

  const allResults = await Promise.all(promises);
  allResults.forEach(({ name, equipment }) => {
    if (equipment.length > 0) {
      results[name] = equipment;
    }
  });

  return results;
};

const calculateStats = (equipment: Equipment[]): EquipmentStats => {
  const stats: EquipmentStats = {
    total: equipment.length,
    byCountry: {},
    byStatus: {},
    byBusinessUnit: {},
  };

  equipment.forEach((eq) => {
    const country = eq.location_country_code || "Unknown";
    const status = eq.functional_object_status || "unknown";
    const bu = eq.business_unit || "Unknown";

    stats.byCountry[country] = (stats.byCountry[country] || 0) + 1;
    stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    stats.byBusinessUnit[bu] = (stats.byBusinessUnit[bu] || 0) + 1;
  });

  return stats;
};

const configLoader = {
  getTechnologies,
  loadAllEquipment,
  calculateStats,
};

export default configLoader;
